import { z } from "zod/v4";
import { defineResource, defineEndpoint } from "@visioni/a3s-client-api";
import { TenantIdCodec } from "@visioni/a3s-schema/codecs";
import { isoDate, stringToInt } from "@visioni/a3s-codecs";
import { FEATURE_NAME } from "../constants";
import { REPORT_CADENCES, REPORT_STATUSES } from "../fleet-services/reports-db/schema";
import { ReportType, TypePath } from "./report-types";

/**
 * GET /v2/tenants/{tenantId}/features/reports/{type}/list
 *
 * Lists every report row for the caller's tenant for the given report type,
 * newest-first. The {type} URL segment is mandatory rather than a query
 * parameter — the listing UI is bespoke per report kind (a WasteSense list and
 * a future RoadSense list render different columns), so the URL itself routes
 * to the right page rather than letting the frontend juggle a discriminated
 * union over every possible report shape.
 *
 * The response intentionally contains only listing-cheap fields. Anything
 * heavy — the full WasteSensePayload, S3 keys to image derivatives — is
 * deferred to the per-item GET so the listing page renders fast on a thin
 * connection. Compare the engine-side ReportEntity attributes
 * (features/a3s-reports/src/fleet-services/reports-db/schema.ts): payloadS3Key
 * / jobS3Key / payloadSize are all stripped from the wire because the listing
 * page never needs them.
 *
 * Pagination is cursor-based by design (matching a3s-media's clip listing):
 * ElectroDB exposes its DDB LastEvaluatedKey as an opaque base64 cursor, and
 * we forward it verbatim as the `cursor` query parameter on the next page.
 * Offset-based pagination would silently desync the moment the daily
 * generator stamps a new row at the front of the index.
 */

export const ListReportsQueryParams = z.object({
  /**
   * Inclusive lower bound on `periodStart` (the report period's first day,
   * `YYYY-MM-DD`). Validated as a calendar date — not a datetime — because
   * `periodStart` is itself stored as a bare calendar string on the row (see
   * design §4.2). Optional; **must be paired with `to`** (see handler).
   */
  from: isoDate.optional(),
  /**
   * Inclusive upper bound on `periodStart`. Both bounds are inclusive: the GSI1
   * "all types" path internally adjusts to a next-day exclusive upper bound to
   * keep that semantics correct (see {@link makeListReports}); the wire-level
   * contract the UI sees is uniformly inclusive on both ends.
   *
   * The `from`/`to` pair is enforced as both-or-neither at the handler layer
   * — accepting a single bound and silently dropping it would return an
   * unfiltered list to a caller who believes it is filtered, which is the
   * worst possible failure mode for a billing/audit-adjacent surface.
   */
  to: isoDate.optional(),
  /**
   * Lifecycle filter. The UI typically passes `"ready"` to hide rows that are
   * still generating or have failed. Off the DDB key (a filter expression)
   * because the rows are tiny and on-key encoding would inflate the SK for
   * every read of every other status.
   */
  status: z.enum(REPORT_STATUSES).optional(),
  /**
   * Page size. Capped at 100 because each row already carries a denormalised
   * `listingSummary` blob whose plugin-defined shape can be a few hundred
   * bytes; 100 rows × O(KB) is the largest single API Gateway response we
   * want to commit to before forcing the caller into a second round-trip.
   * `stringToInt` decodes the URL string into a bounded integer at the edge.
   */
  limit: stringToInt.pipe(z.int().positive().max(100)).optional(),
  /**
   * Opaque ElectroDB cursor copied verbatim from the previous page's response.
   * Treated as an unparsed string at this layer — the value's structure is an
   * implementation detail of the DDB index it was issued against and must not
   * leak into client logic.
   */
  cursor: z.string().optional(),
});
export type ListReportsQueryParams = z.output<typeof ListReportsQueryParams>;

/**
 * One row in the listing response.
 *
 * Mirrors the listing-relevant subset of {@link Report} (see reports-db/schema)
 * without leaking any S3 keys or attempt counters: the listing UI does not
 * need them, and exposing them would couple the frontend to the engine's
 * private storage layout. Fields here are 1:1 with the design's §4.4 example.
 *
 * Wire shape notes:
 * - `tenantId` round-trips through {@link TenantIdCodec} so the consumer sees
 *   a branded `TenantId`, not a bare string.
 * - `periodStart` / `periodEnd` are calendar dates (`YYYY-MM-DD`), validated
 *   by `isoDate`. They are NOT instants — the report period is a local-day
 *   window owned by the generator, not a wall-clock timestamp.
 * - `generatedAt` IS an instant: it is the moment the generator marked the
 *   row ready, used by the UI as a freshness indicator. Only present once
 *   `status === "ready"`, hence optional on the wire.
 * - `listingSummary` is plugin-defined and the engine stores it verbatim
 *   (see ReportEntity's `any` typing). We type it as `z.json()` — the
 *   recursive JSON-value shape — rather than `record(string, unknown)` for
 *   two reasons: (a) it's the truthful contract (the value travels through
 *   `JSON.stringify`/`JSON.parse` end-to-end so non-JSON values are not
 *   actually possible) and (b) `unknown` at the leaves leaks `undefined`,
 *   which Powertools' `JSONValue` response constraint correctly rejects.
 *   Adding a new field for a new plugin remains a zero-touch operation here.
 *   The frontend narrows on `type` before reading it.
 * - `warnings` is always an array (engine-side it's an ElectroDB string set
 *   that's only written when non-empty; we normalise to `[]` on egress to
 *   spare the renderer an `?? []`).
 */
export const ReportListItem = z.object({
  tenantId: TenantIdCodec,
  type: ReportType,
  cadence: z.enum(REPORT_CADENCES),
  reportId: z.string(),
  periodStart: isoDate,
  periodEnd: isoDate,
  status: z.enum(REPORT_STATUSES),
  payloadSchemaVersion: z.int(),
  customerName: z.string(),
  generatedAt: z.iso.datetime().optional(),
  listingSummary: z.json(),
  warnings: z.array(z.string()),
});
export type ReportListItem = z.output<typeof ReportListItem>;

/**
 * Standard a3s `{ data, cursor? }` envelope. Not built via the
 * `CollectionResponse` factory in `@visioni/a3s-client-api` because that
 * helper hard-requires a `subject: CameraSubject` field on every item — a
 * camera-scoped concept that doesn't apply to tenant-scoped reports (a row's
 * "subject" is the tenant + report period, not a single camera).
 */
export const ListReportsResponse = z.object({
  data: z.array(ReportListItem),
  cursor: z.string().optional(),
});
export type ListReportsResponse = z.output<typeof ListReportsResponse>;

/**
 * Resource template `{type}/list`.
 *
 * Placing `{type}` first (rather than e.g. `list?type=...`) makes the URL
 * itself the routing key for the bespoke per-type pages: each report kind's
 * frontend route mounts its own data hook against this exact path, and a new
 * report type is one new {type} value here without revisiting the frontend
 * router.
 *
 * `defineResource` requires `resourcePathSchema` whenever the template
 * contains a `{name}` placeholder; `TypePath` validates the {type} segment as
 * a `ReportType` enum so a bogus URL is rejected at the edge with a 400
 * before the handler is reached.
 */
export const reportsListResource = defineResource({
  feature: FEATURE_NAME,
  resource: "{type}/list",
  resourcePathSchema: TypePath,
  scope: "tenant",
});

export const listReportsEndpoint = defineEndpoint(reportsListResource, {
  responseSchema: ListReportsResponse,
  querySchema: ListReportsQueryParams,
});
